import React, { useState, useEffect } from 'react';
import './CareerForm.css';

// Import necessary components from the components directory
import { Heading, PageTitle } from '../../../components';

import { FaUser, FaEnvelope, FaBriefcase, FaCommentDots, FaAngleDown, FaCertificate, FaPhoneAlt } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';
import axios from "axios";

// API endpoint for submitting the career form
const API_PATH = 'https://www.example.com/api/phpmailer/career.php';

// Regex patterns for email and phone number validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const CareerForm = () => {

  // State to manage form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    position: '',
    location: '',
    education: '',
    experience: '',
    message: ''
  });

  // State for tracking form submission status and errors
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Effect hook to automatically hide success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  /**
   * Handles form submission
   * - Validates all fields
   * - Sends data to API if validation passes
   * - Handles success/error responses
   */
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Validate all form fields
      const validationErrors = [];

      // Check each field and add appropriate error messages
      if (!formData.name.trim()) validationErrors.push('Full name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.position) validationErrors.push('Position is required');
      if (!formData.location.trim()) validationErrors.push('Location is required');
      if (!formData.education.trim()) validationErrors.push('Highest qualification is required');
      if (!formData.experience || formData.experience < 0) validationErrors.push('Valid years of experience is required');
      if (!formData.message.trim()) validationErrors.push('Message is required');

      // If validation errors exist, throw them
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data for submission
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // Send data to API
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle API response
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Application submission failed');
      }

      // On success: show success message and reset form
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        position: '',
        location: '',
        education: '',
        experience: '',
        message: ''
      });
    } catch (err) {
      // Handle errors from validation or API
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      // Reset loading state
      setIsSubmitting(false);
    }
  };

  /**
   * Handles input changes for all form fields
   * Updates the formData state as user types
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <PageTitle title={'Career Form'} page={'Career Form'} />

      <section className="custom-form career-form">
        <div className="box-container">
          <div className="content">
            <form className="dark-form" onSubmit={handleSubmitForm}>
              <Heading title={"Career Application"} subTitle={"Connect With Us"} linear sideHeading />

              <div className="input-box">

                {/* Name input */}
                <div className="input-field">
                  <label htmlFor="name"><FaUser /></label>
                  <input
                    type="text"
                    name="name"
                    className="box"
                    id="name"
                    placeholder="Your Full Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {/* Email input */}
                <div className="input-field">
                  <label htmlFor="email"><FaEnvelope /></label>
                  <input
                    type="email"
                    name="email"
                    className="box"
                    id="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

              </div>

              <div className="input-box">

                {/* Phone number input */}
                <div className="input-field">
                  <label htmlFor="number"><FaPhoneAlt /></label>
                  <input
                    type="number"
                    name="number"
                    className="box"
                    id="number"
                    placeholder="Your Phone Number"
                    value={formData.number}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {/* Position dropdown */}
                <div className="select">
                  <div className="select-container">
                    <label htmlFor="position"><FaBriefcase /></label>
                    <select
                      className="select-box"
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">Select Position</option>
                      <option value="Field Technician">Field Technician</option>
                      <option value="Network Engineer">Network Engineer</option>
                      <option value="Customer Support">Customer Support</option>
                      <option value="Sales Representative">Sales Representative</option>
                      <option value="Installation Specialist">Installation Specialist</option>
                      <option value="IT Support">IT Support</option>
                      <option value="Project Manager">Project Manager</option>
                      <option value="Other">Other</option>
                    </select>
                    <div className="icon-container"><FaAngleDown className="icon" /></div>
                  </div>
                </div>

              </div>

              <div className="input-box">
                {/* Location input */}
                <div className="input-field">
                  <label htmlFor="location"><FaLocationDot /></label>
                  <input
                    type="text"
                    name="location"
                    className="box"
                    id="location"
                    placeholder="Your City/State"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="input-box">

                {/* Education input */}
                <div className="input-field">
                  <label htmlFor="education"><FaCertificate /></label>
                  <input
                    type="text"
                    name="education"
                    className="box"
                    id="education"
                    placeholder="Highest Qualification"
                    value={formData.education}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {/* Experience input */}
                <div className="input-field">
                  <label htmlFor="experience"><FaBriefcase /></label>
                  <input
                    type="number"
                    name="experience"
                    className="box"
                    id="experience"
                    placeholder="Years of Experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

              </div>

              {/* Message textarea */}
              <div className="input-field text-area">
                <label htmlFor="message"><FaCommentDots /></label>
                <textarea
                  cols="30"
                  rows="6"
                  name="message"
                  className="box"
                  id="message"
                  placeholder="Tell us about your telecom experience and why you'd be a great fit for our team"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                ></textarea>
              </div>

              {/* Submit button with loading state */}
              <button 
                type="submit" 
                className="btn" 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Apply Now'}
              </button>

              {/* Error display - shows validation or API errors */}
              {error && (
                <div className="alert error">
                  {error.split(' | ').map((msg, i) => (
                    <span key={i}>{msg}</span>
                  ))}
                </div>
              )}

              {/* Success message display */}
              {mailSent && <div className="alert success"><span>Application Submitted Successfully!</span></div>}

            </form>
          </div>
        </div>
      </section>

    </>
  );
}

export default CareerForm;